import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Alert, LinearProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { authGet, authPut, authPost } from '../../services/apiService';
import H22026SubCategoryAdmin from './H22026SubCategoryAdmin';

const darkColor = '#0f1f3d';

const PILLARS = [
  {
    field: 'operationalComplianceEpicId',
    label: 'Operational Compliance',
    helper: 'Epic ID or ADO URL for the Operational Compliance pillar',
  },
  {
    field: 'azureGovComplianceEpicId',
    label: 'Azure Gov Compliance',
    helper: 'Epic ID or ADO URL for the Azure Gov Compliance pillar',
  },
];

const H22026Admin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig]   = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError]     = useState(null);

  const [syncingTags, setSyncingTags]     = useState(false);
  const [syncResult, setSyncResult]       = useState(null);
  const [syncError, setSyncError]         = useState(null);

  const [epicIds, setEpicIds] = useState({
    operationalComplianceEpicId: '',
    azureGovComplianceEpicId:    '',
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const res = await authGet('/api/compliance-h2-2026/config');
      if (res.success) {
        setEpicIds({
          operationalComplianceEpicId: res.config.operationalComplianceEpicId || '',
          azureGovComplianceEpicId:    res.config.azureGovComplianceEpicId    || '',
        });
      } else {
        setConfigError('Failed to load configuration');
      }
    } catch (e) {
      setConfigError(e.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSave = async () => {
    try {
      setSavingConfig(true); setConfigError(null); setConfigSuccess(null);
      const res = await authPut('/api/compliance-h2-2026/config', epicIds);
      if (res.success) {
        setConfigSuccess('Configuration saved successfully');
        setTimeout(() => setConfigSuccess(null), 5000);
      } else {
        setConfigError(res.message || 'Failed to save configuration');
      }
    } catch (e) {
      setConfigError(e.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSyncTags = async () => {
    try {
      setSyncingTags(true); setSyncError(null); setSyncResult(null);
      const res = await authPost('/api/compliance-h2-2026/sync/feature-tags', {});
      if (res.success) {
        setSyncResult(res.message || 'Tags synced successfully');
        setTimeout(() => setSyncResult(null), 8000);
      } else {
        setSyncError(res.message || 'Failed to sync tags');
      }
    } catch (e) {
      setSyncError(e.response?.data?.message || 'Failed to sync tags from ADO');
    } finally {
      setSyncingTags(false);
    }
  };

  const disabled = loadingConfig || savingConfig;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 1, color: darkColor }} />
        <Typography variant="h5" component="h1">
          H2-2026 Planning &amp; Delivery — Configuration
        </Typography>
      </Box>

      {loadingConfig ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Azure DevOps Epic Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set the Azure DevOps Epic ID (or full ADO URL) for each compliance pillar. The dashboard
                and Product Engineering tab compute progress from features under each Epic.
              </Typography>

              <Grid container spacing={2}>
                {PILLARS.map(({ field, label, helper }) => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth label={label} value={epicIds[field]}
                      onChange={e => setEpicIds(prev => ({ ...prev, [field]: e.target.value }))}
                      placeholder="Enter Epic ID or ADO URL"
                      helperText={helper} disabled={disabled} size="small"
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <Button variant="contained" onClick={handleSave} disabled={disabled} startIcon={<SaveIcon />}
                      sx={{ bgcolor: darkColor, '&:hover': { bgcolor: '#080f1e' } }}>
                      {savingConfig ? 'Saving…' : 'Save Configuration'}
                    </Button>
                    <Button variant="outlined" onClick={handleSyncTags} disabled={syncingTags || loadingConfig} startIcon={<SyncIcon />}
                      sx={{ borderColor: darkColor, color: darkColor, '&:hover': { bgcolor: `${darkColor}0d` } }}>
                      {syncingTags ? 'Syncing tags…' : 'Sync Tags from ADO'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {configSuccess && <Alert severity="success" sx={{ mt: 2 }}>{configSuccess}</Alert>}
              {configError   && <Alert severity="error"   sx={{ mt: 2 }}>{configError}</Alert>}
              {syncResult    && <Alert severity="success" sx={{ mt: 2 }}>{syncResult} — reload Product Engineering and Delivery tabs to see updated data.</Alert>}
              {syncError     && <Alert severity="error"   sx={{ mt: 2 }}>{syncError}</Alert>}

              <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'info.50' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">About the Epics:</Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {PILLARS.map(({ label }) => (
                    <li key={label}><Typography variant="body2">{label}</Typography></li>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Progress, features, and story points are computed from Features and Backlog Items under each Epic.
                  Tags on features determine their initiative grouping (Product Engineering tab) and delivery schedule.
                  Use <strong>Sync Tags from ADO</strong> if a feature tag was added in ADO but is not yet reflected.
                </Typography>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Box mt={4}>
        <H22026SubCategoryAdmin />
      </Box>
    </Box>
  );
};

export default H22026Admin;
